/**
 * Wohin ein Analyse-Upload gehen darf — als Context statt als Prop-Kette.
 *
 * `QuestionField` steckt acht `QuestionBlock`-Verwender tief. Ein
 * `uploadTarget`-Prop müsste durch jede dieser Ebenen gereicht werden, und
 * genau eine vergessene Weitergabe hiesse: Datei-Slot ohne Ziel.
 *
 * Deshalb ein Context mit der SICHEREN Vorgabe `{ mode: "readonly" }`. Wer
 * nichts tut, bekommt eine reine Anzeige — die Zusammenfassung, jede künftige
 * Einbettung. Schreiben kann nur, wer den Provider bewusst setzt (AP4:
 * Beratermodus, AP7: öffentliches Token-Formular).
 */
import * as React from "react";

export type UploadTarget =
  /** Eingeloggter Berater; Ziel ist der Kontakt der Analyse. */
  | { mode: "internal"; contactId: number }
  /** Öffentliches Formular ohne CRM-Login; der Token trägt Mandant und Kontakt. */
  | { mode: "token"; token: string }
  /** Nur ansehen — keine Upload-, keine Entfernen-Aktion. */
  | { mode: "readonly" };

export const READONLY_UPLOAD_TARGET: UploadTarget = { mode: "readonly" };

const UploadTargetContext = React.createContext<UploadTarget>(READONLY_UPLOAD_TARGET);

export const UploadTargetProvider = ({
  target,
  children,
}: {
  target: UploadTarget;
  children: React.ReactNode;
}) => (
  <UploadTargetContext.Provider value={target}>{children}</UploadTargetContext.Provider>
);

/** Das aktuelle Upload-Ziel; ohne Provider immer `readonly`. */
export const useUploadTarget = (): UploadTarget => React.useContext(UploadTargetContext);

/**
 * Darf an diesem Ziel überhaupt geschrieben werden?
 *
 * Beantwortet nur die Frage des Contexts. Ob ein Slot den Kanal auch WIRKLICH
 * bedienen kann, entscheidet `DocumentUploadField`: `internal` ist mit AP4
 * scharf, `token` wartet auf AP7 (eigener Nachreich-Lesepfad, eigener
 * Fehlerdialog) und bleibt bis dahin reine Anzeige.
 */
export const kannHochladen = (target: UploadTarget): boolean =>
  target.mode !== "readonly";
